import { Injectable, Injector } from '@angular/core';
import { Headers, Http } from '@angular/http';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import 'rxjs/add/operator/finally';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/switchMap';
import moment from 'moment-es6';
import { Observable } from 'rxjs/Observable';
import { UserModel } from '../models/user.model';
import { ConfigService } from '../../config/config.service';
import { MultiTenantService } from './multi-tenant.service';
import { ToastService } from '../../toast/services/toast.service';
import { RouteService } from '../../routing/services/route.service';
import { __, trans } from '../../localization/helpers';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';

const toJson = (result) => {
    return result.json();
};

@Injectable()
export class AuthenticationService {

    public static IMPERSONATION_AUTH_TOKEN = 'impersonated_auth_token';

    public static AUTH_TOKEN = 'auth_token';

    /**
     * The api url.
     */
    public apiUrl;

    /**
     * The observable to listen to token changes.
     */
    public tokenTrigger: BehaviorSubject<any>;

    protected tokenName: string;

    /**
     * The timestamp of the last user update.
     *
     * @type {moment.Moment}
     */
    protected lastUserUpdate = moment();

    protected impersonatedUserInstance: UserModel | null = null;

    protected tenantService: MultiTenantService;

    /**
     * The subject which contains the user model.
     *
     * @type {ReplaySubject<any>}
     */
    public user$: ReplaySubject<UserModel> = new ReplaySubject(1);

    protected toastService: ToastService;

    private refreshAttempts      = 0;
    private refreshAttemptsLimit = 1;

    public startingImpersonation$ = new Subject();

    public stoppingImpersonation$ = new Subject();

    /**
     * The chassis service has to be retrieved through the constructor,
     * otherwise the service won't be available.
     *
     * @param {Http} http
     * @param {Injector} injector
     * @param config
     */
    public constructor(private http: Http, private injector: Injector, private config: ConfigService) {
        this.tenantService = this.injector.get(MultiTenantService);
        this.toastService  = this.injector.get(ToastService);

        // Set base api url
        this.apiUrl = this.config.get('api_url') || '';

        // Set the token name
        this.tokenName = this.impersonationIsActive() ? AuthenticationService.IMPERSONATION_AUTH_TOKEN : AuthenticationService.AUTH_TOKEN;

        // Set token trigger
        this.tokenTrigger = new BehaviorSubject(this.getToken());

        if (this.check()) {
            this.requestUser().catch(() => {
            });
        }

        this.user$.subscribe((user: UserModel) => {
            this.tenantService.onScopeChange().subscribe((scope) => {
                user.tenant_scope = scope;
            });
        });

        if (!!localStorage.getItem('auth_session_expired')) {
            this.toastService.info(null, trans('Session has expired, please login'), {timeout: 8 * 1000});
            localStorage.removeItem('auth_session_expired');
        }
    }

    /**
     * Get the current authentication token.
     *
     * @returns {string}
     */
    public getToken() {
        return localStorage.getItem(this.tokenName);
    }

    /**
     * Save the given token to the local storage.
     *
     * @param {string} token
     */
    private setToken(token: string): void {
        localStorage.setItem(this.tokenName, token);

        this.tokenTrigger.next(token);
    }

    /**
     * Check if the user is logged in.
     *
     * @returns {string}
     */
    public check() {
        return !!this.getToken();
    }

    /**
     * Request the user instance from the backend.
     */
    protected requestUser(): Promise<UserModel> {
        return new Promise((resolve, reject) => {
            const options = {
                headers: this.getHeaders()
            };

            // Request the current authenticated user
            const request = this.http.get(this.apiUrl + '/me', options).map(toJson);

            request.subscribe((user: UserModel) => {
                // Update last update timestamp
                this.lastUserUpdate = moment();

                // Create user model with the retrieved data
                // const userService: UserService = this.injector.get(UserService);

                // Create a new user instance
                const instance: UserModel = UserModel.make(user['data'], null);

                if (instance.tenant_scope) {
                    if (instance.hasRole('admin')) {
                        this.tenantService.disable();
                    } else {
                        this.tenantService.setCurrentScope(instance.tenant_scope);
                        this.tenantService.enable();
                    }
                } else {
                    this.tenantService.disable();
                }

                // Push the user to the user subject subscribers
                this.user$.next(instance);

                resolve(instance);
            }, (error) => {
                error.body = error.json();

                console.error('Auth error!', error);
                // If the error status is zero no connection could
                // be made, in this case we will show a message to
                // the user.
                if (error.status === 0) {
                    this.toastService.error('error', trans('Connection to the server was lost, please try again'), {
                        timeout: 0
                    });
                }

                if (error.status === 401) {
                    console.log('auth', error);
                    // if (error.body.message === 'Token has expired') {
                    if (this.refreshAttempts < this.refreshAttemptsLimit) {
                        this.refreshAttempts++;

                        return this.refreshTokenAndRequestUser()
                            .catch((error) => {
                                reject(error);
                            });
                    }
                    // }

                    // Reset refresh attempts
                    this.refreshAttempts = 0;

                    reject(error);

                    this.redirectToLogin();
                    return;
                }

                reject(error);
            });
        });
    }

    protected refreshTokenAndRequestUser() {
        return new Promise((resolve, reject) => {
            this.refreshToken().subscribe((token: string) => {
                this.addTokenToHeaders(token);

                // Retry the request
                this.requestUser().catch(() => {
                });

                resolve();
            }, error => {
                this.user$.error('Cannot refresh token: ' + error);

                reject(error);

                this.logout();
            });
        });
    }

    public redirectToLogin() {
        this.logout();

        const route = this.injector.get(RouteService);
        return route.navigate('login');
    }

    protected addTokenToHeaders(token: string) {
        this.http['_defaultOptions'].headers.set('Authorization', 'Bearer ' + token);
    }

    /**
     * Get token, only for backward compatibility.
     *
     * @deprecated
     */
    public isLoggedIn() {
        return this.getToken();
    }


    /**
     * Perform a login attempt and return the observable
     *
     * @param email
     * @param password
     * @param remember
     * @param impersonation
     * @returns {Observable}
     */
    public login(email, password, remember = true, impersonation = false) {

        this.resetOriginalUser();

        return new Observable(observer => {
            const body = {
                email: email,
                password: password,
                remember: remember,
                impersonation: impersonation,
            };

            // Send authentication request
            this.http.post(this.apiUrl + '/authenticate', body).map(toJson).subscribe((result) => {
                // Set token
                this.setToken(result['token']);

                // Set Firebase token
                this.setFirebaseToken(result['firebase_token']);

                // Get the authenticated user
                this.requestUser().then((user: UserModel) => {
                    observer.next(user);
                });
            }, (error) => {
                observer.error(error.json());
            });
        });
    }

    /**
     * Exists for backward compatibility.
     *
     * @returns {Observable}
     * @deprecated
     */
    public getUser() {
        return this.user();
    }

    /**
     * Get an observable with the current user.
     *
     * @returns {ReplaySubject<UserModel>}
     */
    public user() {
        return this.user$;
    }

    /**
     * Refresh JWT and Firebase tokens
     *
     * @returns {Promise<T>}
     */
    public refreshToken() {
        return new Observable(observer => {
            const options = {
                headers: this.getHeaders()
            };

            this.http.post(this.apiUrl + '/refresh', {}, options).map(toJson).subscribe((response) => {
                const refreshToken = response['refresh_token'];

                // Set new token
                this.setToken(refreshToken);

                const firebaseToken = response['firebase_token'];

                // Set new Firebase token
                this.setFirebaseToken(firebaseToken);

                // Return the new token
                observer.next(refreshToken);
            }, (error) => {
                if (error.status === 401) {
                    console.log('Redirect to login due to token refresh error');

                    localStorage.removeItem('auth_token');
                    localStorage.setItem('auth_session_expired', 'true');
                    window.location.reload(true);

                    observer.error(error);

                    this.redirectToLogin();
                }

                observer.error(error);
            }, () => {
                observer.complete();
            });
        });
    }

    /**
     * Remove the authentication token.
     */
    public logout(): Promise<any> {
        return new Promise((resolve, reject) => {
            // Complete the subject so subscribers will be unsubscribed
            this.user$.complete();

            // Remove the local token
            localStorage.removeItem(this.tokenName);

            // Create a clear subject
            this.user$ = new ReplaySubject(1);

            resolve();
        });
    }

    /**
     * Send an email with a password reset code to the given email address.
     *
     * @param data
     * @return {Observable<any>}
     */
    public sendPasswordResetCode(data) {
        return new Observable((observer) => {
            const request = this.http.post(this.apiUrl + '/authentication/forgot-password', data).map(toJson);

            request.subscribe((response) => {
                observer.next(response);

                observer.complete();
            }, (error) => {
                observer.error(error.json());
            });
        });
    }

    /**
     * Confirm the password reset and save the new password.
     *
     * @param data
     * @return {Observable<any>}
     */
    public resetPassword(data) {
        return new Observable((observer) => {
            const request = this.http.post(this.apiUrl + '/authentication/reset-password', data).map(toJson);

            request.subscribe((response) => {
                observer.next(response);

                observer.complete();
            }, (error) => {
                error = JSON.parse(error._body);
                observer.error(error);
            });
        });
    }

    public updateUser(userObject: any) {

        return new Observable(observer => {

            this.user$.take(1).subscribe(user => {
                userObject = Object.assign(user.plain(), userObject);

                this.http
                    .put(this.apiUrl + '/me', userObject)
                    .map(toJson)
                    .subscribe((response) => {

                        // Create a new user instance
                        const instance: UserModel = UserModel.make(response['data'], null);

                        this.user$.next(instance);

                        observer.next(instance);
                        observer.complete();
                    }, error => {
                        observer.error(error);
                        observer.complete();
                    });
            });

        });
    }

    /**
     * Get the request headers.
     *
     * @returns {Headers}
     */
    protected getHeaders() {
        const headers = new Headers();

        const token = this.getToken();

        // Append the token if set
        if (token) {
            headers.append('Authorization', 'Bearer ' + token);
        }

        if (this.tenantService.isEnabled()) {
            headers.append('X-TenantScope', this.tenantService.getCurrentScope());
        }

        return headers;
    }

    /**
     * Save the given Firebase token to the local storage.
     *
     * @param {string} token
     */
    private setFirebaseToken(token: string): void {
        localStorage.setItem('firebase_token', token);
    }

    /**
     * Start impersonating the given user.
     *
     * @param user
     */
    public impersonate(user): Promise<UserModel> {
        this.startingImpersonation$.next();

        return new Promise((resolve, reject) => {
            this.tokenName = AuthenticationService.IMPERSONATION_AUTH_TOKEN;

            const request = this.login(user.email, '', true, true);

            this.setOriginalUser();

            request.subscribe((user: UserModel) => {
                this.setImpersponatedUser(user);

                const router = this.injector.get(RouteService);
                router.navigate('home');

                this.toastService.success('success', __('You are now impersonating {{user}}', {user: user.name}));

                resolve(user);
            }, (error) => {
                this.tokenName = AuthenticationService.AUTH_TOKEN;

                console.error(error);
                this.toastService.error('error', __('Cannot impersonate user'));

                reject(error);
            });
        });
    }

    public stopImpersonating() {
        this.stoppingImpersonation$.next();

        return new Promise((resolve, reject) => {
            localStorage.removeItem(this.tokenName);
            this.resetOriginalUser();

            this.tokenName = AuthenticationService.AUTH_TOKEN;

            this.setToken(this.getToken());

            this.requestUser().then((user: UserModel) => {

                this.impersonatedUserInstance = null;

                this.toastService.success('success', __('Impersonation has been ended for {{user}}', {user: user.name}));

                const router = this.injector.get(Router);
                router.navigateByUrl('/')
                    .then(() => {
                        resolve();
                    }, (error) => {
                        reject();
                    });
            }, () => {
                this.toastService.error('error');

                reject();
            });
        });
    }

    public impersonationIsActive() {
        return localStorage.getItem('impersonated_auth_token');
    }

    public getOriginalUser() {
        return JSON.parse(localStorage.getItem('original_user'));
    }

    protected getImpersponatedUser() {
        return this.impersonatedUserInstance;
    }

    protected setImpersponatedUser(user) {
        this.impersonatedUserInstance = user;
    }

    protected setOriginalUser() {
        this.user$.take(1).subscribe(user => {
            localStorage.setItem('original_user', JSON.stringify(user.plain()));
        });
    }

    protected resetOriginalUser() {
        localStorage.removeItem('original_user');
    }
}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/authentication/services/authentication.service.ts